import React, { forwardRef, useImperativeHandle } from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export const FinancialInformationForm = forwardRef(({ dto, handleInputChange, handleDirectInputChange }, ref) => {
    const { t } = useTranslation();
    const [errorInputs, handleErrorInput] = useErrorInputs({});

    const validateField = (name, value) => {
        if (typeof value === 'string' && value.trim() === '') {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        }

        if (name === "cutoff_date_information" && isNaN(new Date(value).getTime())) {
            handleErrorInput(name, { isError: true, messageError: t("validation.invalidDate") });
            return false;
        }

        handleErrorInput(name, { isError: false, messageError: '' });
        return true;
    };

    useImperativeHandle(ref, () => ({
        validateForm,
    }));

    const requiredField = ['total_assets', 'total_liabilities', 'patrimony', 'monthly_income', 'expenses', 'other_income', 'cutoff_date_information'];
    const validateForm = () => {
        let hasError = false;
        requiredField.forEach((field) => {
            const isValid = validateField(field, dto[field]);
            if (!isValid) hasError = true;
        });
        return !hasError;
    };

    const handleChangeDate = (e, name) => {
        const dateValue = moment(e).format('YYYY/MM/DD');
        handleDirectInputChange('cutoff_date_information', dateValue)
        validateField(name, dateValue);
    };


    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="h6">{t("suppliers.financialInformationManual")}</Typography>
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    label={t("suppliers.financial.totalAssets")}
                    name="total_assets"
                    value={dto.total_assets}
                    onChange={handleInputChange}
                    type="number"
                    size='small'
                    error={Boolean(errorInputs.total_assets?.isError)}
                    helperText={errorInputs.total_assets?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    label={t("suppliers.financial.totalLiabilities")}
                    name="total_liabilities"
                    onChange={handleInputChange}
                    type='number'
                    size='small'
                    value={dto.total_liabilities}
                    error={Boolean(errorInputs.total_liabilities?.isError)}
                    helperText={errorInputs.total_liabilities?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    label={t("suppliers.financial.patrimony")}
                    name="patrimony"
                    value={dto.patrimony}
                    onChange={handleInputChange}
                    type="number"
                    size='small'
                    error={Boolean(errorInputs.patrimony?.isError)}
                    helperText={errorInputs.patrimony?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    label={t("suppliers.financial.monthlyIncome")}
                    name="monthly_income"
                    value={dto.monthly_income}
                    onChange={handleInputChange}
                    type="number"
                    size='small'
                    error={Boolean(errorInputs.monthly_income?.isError)}
                    helperText={errorInputs.monthly_income?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    label={t("suppliers.financial.expenses")}
                    name="expenses"
                    value={dto.expenses}
                    onChange={handleInputChange}
                    type="number"
                    size='small'
                    error={Boolean(errorInputs.expenses?.isError)}
                    helperText={errorInputs.expenses?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 4, sm: 4 }}>
                <TextField
                    fullWidth
                    label={t("suppliers.financial.otherIncome")}
                    name="other_income"
                    value={dto.other_income}
                    onChange={handleInputChange}
                    type="number"
                    size='small'
                    error={Boolean(errorInputs.other_income?.isError)}
                    helperText={errorInputs.other_income?.messageError || ''}
                    required
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        name="cutoff_date_information"
                        label={t("suppliers.financial.cutoffDate")}
                        format="YYYY/MM/DD"
                        maxDate={new Date()}
                        value={dto.cutoff_date_information}
                        onChange={(e) => handleChangeDate(e, 'cutoff_date_information')}
                        slotProps={{
                            textField: {
                                id: "cutoff_date_information",
                                name: "cutoff_date_information",
                                size: "small",
                                fullWidth: true,
                                required: true,
                                error: Boolean(errorInputs.cutoff_date_information?.isError),
                                helperText: errorInputs.cutoff_date_information?.messageError,
                                InputLabelProps: { shrink: true }
                            }
                        }}
                    />
                </LocalizationProvider>
            </Grid>

        </Grid>
    );
});
